import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck } from '@fortawesome/pro-light-svg-icons';

// services
import ValidationService from '../../../../../services/validation';
import OrderContainerService from '../../../../../services/orders/orderContainer';
import OrderTruckService from '../../../../../services/orders/trucks';
import OrderGoodsService from '../../../../../services/orders/orderGoods';
import GeneralService from '../../../../../services/general';
import PermissionsService from '../../../../../services/permissions';
import PricingService from '../../../../../services/general/pricing';

// props
import { IOrderContainerResourceShortProps } from '../../../../../props/orders/containers';
import { IOrderTruckResourceShortProps } from '../../../../../props/orders/trucks';
import { IOrderGoodsResourceShortProps } from '../../../../../props/orders/orderGoods';

// components
import { Checkbox, DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import { PPNPercentageOptions, PPhPercentageOptions } from '../../../../../manifests/taxPercentage';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import Details from '../../../../typography/details';
import OrderPriceUpdateService from '../../../../../services/orders/priceUpdates';

type RequestOrderPriceUpdateFormProps = {
    orderId: string;
    containerId?: string;
    truckId?: string;
    goodsId?: string;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    price: string;
    quantityAmount?: string;
    pricingMethod?: string;
    includePPn?: boolean;
    includePPh?: boolean;
    ppn: string;
    ppnPercentage: string;
    pph: string;
    pphPercentage: string;
    finalPrice: string;
    nettPrice: string;
    invoicePrice: string;
}

const RequestOrderPriceUpdateForm: React.FC<RequestOrderPriceUpdateFormProps> = (props: RequestOrderPriceUpdateFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);

    const [container, setContainer] = React.useState<IOrderContainerResourceShortProps | undefined>();
    const [truck, setTruck] = React.useState<IOrderTruckResourceShortProps | undefined>();
    const [goods, setGoods] = React.useState<IOrderGoodsResourceShortProps | undefined>();

    const [data, setData] = React.useState<FormDataProps>({
        price: "",
        ppn: "0",
        pph: "0",
        ppnPercentage: "0",
        pphPercentage: "0",
        finalPrice: "0",
        nettPrice: "0",
        invoicePrice: "0"
    });

    const [error, setError] = React.useState<any>({});

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        let _data = data;

        if (props.containerId) {
            const _container = await OrderContainerService.get(props.orderId, props.containerId);
            setContainer(_container);

            _data = {
                price: Number(_container.price) + "",
                ppn: Number(_container.ppn) + "",
                pph: Number(_container.pph) + "",
                ppnPercentage: Number(_container.ppnPercentage) + "",
                pphPercentage: Number(_container.pphPercentage) + "",
                finalPrice: _container.totalPrice + "",
                nettPrice: _container.nettPrice + "",
                invoicePrice: _container.invoicePrice + "",
                includePPn: _container.includePPn,
                includePPh: _container.includePPh
            };
        } else if (props.truckId) {
            const _truck = await OrderTruckService.get(props.orderId, props.truckId);
            setTruck(_truck);

            _data = {
                price: Number(_truck.price) + "",
                ppn: Number(_truck.ppn) + "",
                pph: Number(_truck.pph) + "",
                ppnPercentage: Number(_truck.ppnPercentage) + "",
                pphPercentage: Number(_truck.pphPercentage) + "",
                finalPrice: _truck.totalPrice + "",
                nettPrice: _truck.nettPrice + "",
                invoicePrice: _truck.invoicePrice + "",
                includePPn: _truck.includePPn,
                includePPh: _truck.includePPh
            };
        } else if (props.goodsId) {
            const _goods = await OrderGoodsService.get(props.orderId, props.goodsId);
            setGoods(_goods);

            _data = {
                price: Number(_goods.price) + "",
                ppn: Number(_goods.ppn) + "",
                pph: Number(_goods.pph) + "",
                ppnPercentage: Number(_goods.ppnPercentage) + "",
                pphPercentage: Number(_goods.pphPercentage) + "",
                finalPrice: _goods.totalPrice + "",
                nettPrice: _goods.nettPrice + "",
                invoicePrice: _goods.invoicePrice + "",
                includePPn: _goods.includePPn,
                includePPh: _goods.includePPh,
                pricingMethod: _goods.pricingMethod,
                quantityAmount: (_goods.quantityAmount || "") + ""
            };
        }

        setData(_data);
        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { price, ppn, pph, includePPn, pricingMethod, includePPh, ppnPercentage, pphPercentage, finalPrice, nettPrice, invoicePrice } = data;

            // create form data
            let fd = new FormData();
            fd.append('orderId', props.orderId);
            if (props.containerId && container) {
                fd.append('containerId', container.id);
            } else if (props.truckId && truck) {
                fd.append('truckId', truck.id);
            } else if (props.goodsId && goods) {
                fd.append('goodsId', goods.id);
            }

            fd.append("totalPrice", finalPrice);
            fd.append("finalPrice", finalPrice);
            fd.append("nettPrice", nettPrice);
            fd.append("invoicePrice", invoicePrice);
            fd.append("price", price);
            fd.append("ppn", ppn);
            fd.append("pph", pph);
            fd.append("includePPn", includePPn + "");
            fd.append("includePPh", includePPh + "");
            fd.append("ppnPercentage", ppnPercentage + "");
            fd.append("pphPercentage", pphPercentage + "");

            if (pricingMethod) {
                fd.append("pricingMethod", pricingMethod);
            }

            await OrderPriceUpdateService.create(fd);

            banner.add({
                key: 'request_order_price_update_success',
                variant: 'success',
                icon: faCheck,
                text: `Request to update price has been submitted`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    const calculateAllPrice = (_data: FormDataProps) => {
        const pricing = PricingService.order.calculate(_data, _data.pricingMethod || 'lumpsum');
        setData({ ..._data, ...pricing });
    }

    return <Panel headerText={'Request Update Price'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Submitting request ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    {props.containerId && container ? <>
                        <Label>Container Details</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Container number' text={container.containerNumber || "-"} style={{ width: '50%' }} />
                            <Details title='Size' text={container.size || "-"} style={{ width: '50%' }} />
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Seal number' text={container.sealNumber || "-"} style={{ width: '50%' }} />
                            <Details title='RO number' text={container.roNumber || "-"} style={{ width: '50%' }} />
                        </Stack>
                    </> : null}
                    {props.truckId && truck ? <>
                        <Label>Truck Details</Label>
                        <Details title='Registration number' text={truck.vehicleRegistrationNumber || "-"} />
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Pick-up address' text={truck.originAddress || "-"} style={{ width: '50%' }} />
                            <Details title='Drop-off address' text={truck.destinationAddress || "-"} style={{ width: '50%' }} />
                        </Stack>
                    </> : null}
                    {props.goodsId && goods ? <>
                        <Label>Truck Details</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Name' text={goods.goods.name || "-"} style={{ width: '50%' }} />
                            <Details title='Quantity' text={goods.quantityAmount + " " + (goods.goods.quantityUnit || "")} style={{ width: '50%' }} />
                        </Stack>
                        <Details title='Notes' text={goods.notes || "-"} />
                    </> : null}
                    {data.pricingMethod ? <>
                        <Stack className={'divider'}></Stack>
                        <Stack.Item>
                            <Dropdown label={"Pricing Method"}
                                selectedKey={data.pricingMethod}
                                options={[
                                    { key: 'lumpsum', text: 'Lump sum' },
                                    { key: 'perquantity', text: 'Per quantity' }
                                ]}
                                onChange={(evt, opt) => {
                                    let _data = data;
                                    _data.pricingMethod = opt?.key as string;
                                    calculateAllPrice(_data);
                                    setData({ ...data });
                                }}
                                disabled={submitting} />
                        </Stack.Item>
                    </> : null}
                    <Stack className={'divider'}></Stack>
                    <Stack.Item>
                        <TextField label={"Price"}
                            value={data.price}
                            onChange={(evt, value) => {
                                if (!isNaN(Number(value))) {
                                    data.price = value || "";

                                    const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                    error.price = validation.message;

                                    setError({ ...error });

                                    calculateAllPrice(data);
                                }
                            }}
                            prefix={"Rp"}
                            description={data.price !== "" ? GeneralService.convertNumberToWords(Number(data.price)) : undefined}
                            errorMessage={error.price}
                            disabled={submitting}
                            required={true} />
                    </Stack.Item>
                    <Stack tokens={{ childrenGap: 20 }} horizontal>
                        <Stack.Item>
                            <Checkbox checked={data.includePPn}
                                label='Include PPn'
                                disabled={submitting}
                                onChange={(ev, checked) => {
                                    data.includePPn = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }} />
                        </Stack.Item>
                        <Stack.Item>
                            <Checkbox checked={data.includePPh}
                                disabled={submitting}
                                label='Include PPh'
                                onChange={(ev, checked) => {
                                    data.includePPh = checked;
                                    setData({ ...data });
                                    calculateAllPrice(data);
                                }} />
                        </Stack.Item>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPn</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPNPercentageOptions}
                                    selectedKey={data.ppnPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.ppn = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.ppnPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    disabled={submitting}
                                    styles={{ root: { width: 100 } }} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.ppn}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.ppn = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.ppn = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.ppn}
                                    disabled={submitting || data.ppnPercentage !== 'custom'} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack tokens={{ childrenGap: 2 }}>
                        <Label size={'small'}>PPh</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack.Item>
                                <Dropdown options={PPhPercentageOptions}
                                    selectedKey={data.pphPercentage}
                                    onChange={(evt, opt) => {
                                        if (opt && opt.key !== 'custom') {
                                            data.pph = (Number(opt.key) / 100 * Number(data.price)).toFixed();
                                        }
                                        data.pphPercentage = (opt?.key || "") as string;

                                        setData({ ...data });
                                        calculateAllPrice(data);
                                    }}
                                    disabled={submitting}
                                    styles={{ root: { width: 100 } }} />
                            </Stack.Item>
                            <Stack.Item grow={1}>
                                <TextField required={true}
                                    value={data.pph}
                                    onChange={(evt, value) => {
                                        if (!isNaN(Number(value))) {
                                            data.pph = value || "";

                                            const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 15 });
                                            error.pph = validation.message;

                                            setData({ ...data });
                                            setError({ ...error });
                                            calculateAllPrice(data);
                                        }
                                    }}
                                    prefix={"Rp"}
                                    errorMessage={error.pph}
                                    disabled={submitting || data.pphPercentage !== 'custom'} />
                            </Stack.Item>
                        </Stack>
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Details title={'Total price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.finalPrice)}`} style={{ width: '50%' }} />
                        <Details title={'Invoice price'} text={`Rp. ${GeneralService.getNumberWithSeparator(data.invoicePrice)}`} style={{ width: '50%' }} />
                    </Stack>
                </> : null
            }
        </Stack>
    </Panel >
};

export default RequestOrderPriceUpdateForm;
